import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { APIURL } from '../../../configApi/apiConfig';
import { exportCallHistoryToCSV } from './callHistoryExcel.util';

/**
 * Hook personalizado para manejar la lógica del Historial de Llamadas
 * Separa completamente la lógica de negocio del renderizado visual (Clean Code)
 */
export function useCallHistory({ extension, isOpen }) {
  const [periodo, setPeriodo] = useState('hoy');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [orden, setOrden] = useState('fecha');
  const [direccion, setDireccion] = useState('DESC');
  const [estadoFiltro, setEstadoFiltro] = useState('todos');
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistorial = useCallback(async () => {
    if (!extension || !isOpen) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const queryParams = {
        orden,
        direccion,
        pagina,
        limite,
      };

      if (periodo === 'rango' && fechaDesde) {
        queryParams.fecha = fechaDesde;
        queryParams.fechaFin = fechaHasta || fechaDesde;
      } else {
        queryParams.periodo = periodo;
      }

      const response = await axios.get(
        APIURL.getHistorialLlamadas(extension, queryParams),
        { headers }
      );

      const res = response?.data || {};
      setData(res.data || []);
      setTotal(Number(res.total || 0));
      setTotalPaginas(Number(res.totalPaginas || 1));
      setResumen(res.resumen || null);
    } catch (err) {
      console.error('Error al cargar historial de llamadas:', err);
      setError(err?.response?.data?.message || 'Error al conectar con el servidor');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [extension, isOpen, periodo, fechaDesde, fechaHasta, orden, direccion, pagina, limite]);

  // Ejecuta la consulta al abrir el modal o cambiar filtros del backend
  useEffect(() => {
    if (isOpen && extension) {
      fetchHistorial();
    }
  }, [isOpen, extension, periodo, fechaDesde, fechaHasta, orden, direccion, pagina, limite, fetchHistorial]);

  // Cambiar período rápido (hoy, ayer, semana, mes) resetea rango y página
  const cambiarPeriodo = useCallback((nuevoPeriodo) => {
    setPeriodo(nuevoPeriodo);
    setFechaDesde('');
    setFechaHasta('');
    setPagina(1);
  }, []);

  // Cambiar rango de fechas personalizado
  const cambiarRangoFechas = useCallback((desde, hasta) => {
    setPeriodo('rango');
    setFechaDesde(desde);
    setFechaHasta(hasta || desde);
    setPagina(1);
  }, []);

  // Cambiar límite de filas por página (25, 50, 100)
  const cambiarLimite = useCallback((nuevoLimite) => {
    setLimite(Number(nuevoLimite) || 25);
    setPagina(1);
  }, []);

  // Cambiar ordenamiento resetea a la primera página
  const cambiarOrden = useCallback((nuevoOrden, nuevaDireccion = 'DESC') => {
    setOrden(nuevoOrden);
    setDireccion(nuevaDireccion);
    setPagina(1);
  }, []);

  // Cambiar filtro por estado
  const cambiarEstadoFiltro = useCallback((nuevoEstado) => {
    setEstadoFiltro(nuevoEstado);
  }, []);

  // Filtrado reactivo en cliente (Búsqueda por texto y Estado)
  const filteredData = useMemo(() => {
    let result = data;

    // Filtro por estado
    if (estadoFiltro && estadoFiltro !== 'todos') {
      result = result.filter((item) => {
        const est = (item.Estado || '').toLowerCase();
        return est.includes(estadoFiltro.toLowerCase());
      });
    }

    // Filtro por término de búsqueda (Cliente, Teléfono, Gestor o Compra)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((item) => {
        const cliente = (item.Cliente || '').toLowerCase();
        const telefono = (item.Telefono || '').toLowerCase();
        const gestor = (item.Gestor || '').toLowerCase();
        const estado = (item.Estado || '').toLowerCase();
        const compra = String(item.idCompra || '').toLowerCase();
        return (
          cliente.includes(term) ||
          telefono.includes(term) ||
          gestor.includes(term) ||
          estado.includes(term) ||
          compra.includes(term)
        );
      });
    }

    return result;
  }, [data, estadoFiltro, searchTerm]);

  // Exportar los datos actuales a Excel (CSV)
  const exportarAExcel = useCallback(() => {
    exportCallHistoryToCSV(filteredData.length > 0 ? filteredData : data, extension, periodo);
  }, [filteredData, data, extension, periodo]);

  return {
    periodo,
    fechaDesde,
    fechaHasta,
    orden,
    direccion,
    estadoFiltro,
    pagina,
    totalPaginas,
    total,
    limite,
    searchTerm,
    setSearchTerm,
    data: filteredData,
    rawCount: data.length,
    resumen,
    loading,
    error,
    setPagina,
    cambiarPeriodo,
    cambiarRangoFechas,
    cambiarLimite,
    cambiarOrden,
    cambiarEstadoFiltro,
    exportarAExcel,
    refetch: fetchHistorial,
  };
}
